import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import { useUserStatus } from '../context/UserStatusContext';
import { useCachedApi } from '../hooks/useCachedApi';
import { initializeDigitalLegacy } from '../services/apiService';
import PassportWidget from '../components/dashboard/PassportWidget';
import EditPassportModal from '../components/dashboard/EditPassportModal';
import DailyReflectionWidget from '../components/dashboard/DailyReflectionWidget';
import PublicPresenceModule from '../components/dashboard/PublicPresenceModule';
import CoreProfileModule from '../components/dashboard/CoreProfileModule';
import PersonalityProfileModule from '../components/dashboard/PersonalityProfileModule';
import NarrativeModule from '../components/dashboard/NarrativeModule';
import CommunicationStyleModule from '../components/dashboard/CommunicationStyleModule';
import StylometryModule from '../components/dashboard/StylometryModule';
import AchievementsModule from '../components/dashboard/AchievementsModule';
import CreativeStudioModal from '../components/studio/CreativeStudioModal';
//import IdentitySnapshot from '../components/IdentitySnapshot';
import MiniRadarChart from '../components/molecules/MiniRadarChart';
import GatedFeature from '../components/atoms/GatedFeature';
import MemoryCoreWidget from '../components/dashboard/MemoryCoreWidget';
import styles from './EgoProfilePage.module.css';

function EgoProfilePage() {
  const { userStatus, refreshUserStatus } = useUserStatus();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const {
    data: digitalLegacy,

    isLoading,
    error,
    refresh: refreshDigitalLegacy,
  } = useCachedApi('/v1/user/digital-legacy', 15000);
  const { data: publicProfiles } = useCachedApi('/v1/profiles', 15000);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStudioOpen, setIsStudioOpen] = useState(false);
  const [studioInitialData, setStudioInitialData] = useState({});

  if (isLoading) return <div className="container">Loading Passport...</div>;
  if (error)
    return (
      <div className="container errorText">
        Error loading your Digital Passport.
      </div>
    );

  // --- Define Verification Levels & Colors ---
  const isPro =
    userStatus?.planTier === 'pro' ||
    userStatus?.planTier === 'founder' ||
    userStatus?.planTier === 'vip';
  let verificationLevel = 1,
    verificationColor = 'gray';

  if (userStatus?.verificationStatus?.hasCompletedBasicTest) {
    verificationLevel = 2;
    verificationColor = 'blue';
  }
  if (userStatus?.verificationStatus?.hasCompletedFullTest) {
    verificationLevel = 3;
    verificationColor = 'gold';
  }

  const moduleStatus = {
    coreProfile: userStatus?.firstName && userStatus?.lastName,
    personality: userStatus?.verificationStatus?.hasCompletedBasicTest,
    narrative: digitalLegacy?.narrativeData?.biography,
    style: digitalLegacy?.communicationStyle?.pacing,
  };

  const nextStepMap = {
    coreProfile: 'Complete Your Core Profile',
    personality: 'Take the Personality Test',
    narrative: 'Add Your Narrative & Experience',
    style: 'Define Your Communication Style',
    default: 'Your Passport is Complete!',
  };

  let recommendedStep = '';
  if (!moduleStatus.coreProfile) recommendedStep = 'coreProfile';
  else if (!moduleStatus.personality) recommendedStep = 'personality';
  else if (!moduleStatus.narrative) recommendedStep = 'narrative';
  else if (!moduleStatus.style) recommendedStep = 'style';

  const isPassportComplete =
    moduleStatus.coreProfile &&
    moduleStatus.personality &&
    moduleStatus.narrative &&
    moduleStatus.style;

  const hasAchievements = userStatus?.achievements?.length >= 6;

  const handleGetStarted = async () => {
    setIsInitializing(true);
    try {
      const accessToken = await getAccessTokenSilently();
      await initializeDigitalLegacy(accessToken);
      await refreshUserStatus();
      refreshDigitalLegacy();
    } catch (err) {
      console.error('Failed to initialize Digital Legacy:', err);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleSave = () => {
    refreshUserStatus();
    refreshDigitalLegacy();
  };

  return (
    <div>
      <div className="page-header">
        <h2>Your Digital Passport</h2>
        {!isPassportComplete && (
          <div className={styles.completenessWrapper}>
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${userStatus?.profileCompleteness || 10}%` }}
                ></div>
              </div>
            </div>
            <span className={styles.progressPercentage}>
              {userStatus?.profileCompleteness || 0}%
            </span>
            {userStatus?.profileCompleteness < 100 && (
              <span className={styles.nextStepText}>
                Next Step:{' '}
                {nextStepMap[recommendedStep] || 'Finalize Your Profile'}
              </span>
            )}
          </div>
        )}
      </div>

      {digitalLegacy ? (
        <>
          <div className={styles.dashboardGrid}>
            <div className={styles.leftPanel}>
              <PassportWidget
                digitalLegacy={digitalLegacy}
                planTier={userStatus?.planTier}
                onEdit={() => setIsEditModalOpen(true)}
              />
              <button
                className={`action-button primary ${styles.chatButton}`}
                onClick={() =>
                  navigate(`/chat/${digitalLegacy.personaId}`, {
                    state: { persona: digitalLegacy, isOwner: true },
                  })
                }
                disabled={!isPassportComplete}
              >
                <i className="fas fa-comment-dots"></i> Chat with Your Passport
              </button>
              <DailyReflectionWidget />
              <PublicPresenceModule
                isPassportComplete={isPassportComplete}
                publicProfiles={publicProfiles}
              />
            </div>
            <div className={styles.rightPanel}>
              <div
                className={styles.studioCtaCard}
                onClick={() => setIsStudioOpen(true)}
              >
                <div className={styles.ctaIcon}>🎨</div>
                <div className={styles.ctaText}>
                  <h4>Creative Studio</h4>
                  <p>
                    Craft posts, generate images, and create content with your
                    persona's unique voice.
                  </p>
                </div>
                <button className="action-button secondary">Open</button>
              </div>
              <div className={styles.moduleGrid}>
                <CoreProfileModule
                  isComplete={moduleStatus.coreProfile}
                  isRecommended={recommendedStep === 'coreProfile'}
                />
                <PersonalityProfileModule
                  digitalLegacy={digitalLegacy}
                  isComplete={moduleStatus.personality}
                  isRecommended={recommendedStep === 'personality'}
                />
                <NarrativeModule
                  digitalLegacy={digitalLegacy}
                  isComplete={moduleStatus.narrative}
                  isRecommended={recommendedStep === 'narrative'}
                  onSave={handleSave}
                />
                <CommunicationStyleModule
                  digitalLegacy={digitalLegacy}
                  isComplete={moduleStatus.style}
                  isRecommended={recommendedStep === 'style'}
                  onSave={handleSave}
                />
                <StylometryModule
                  digitalLegacy={digitalLegacy}
                  onSave={handleSave}
                />
                <AchievementsModule isComplete={hasAchievements} />
              </div>
            </div>
          </div>

          {digitalLegacy.personalityData?.openness && (
            <>
              {/* <IdentitySnapshot digitalLegacy={digitalLegacy} /> */}

              <div
                id="deep-personality-facets"
                className={styles.facetsContainer}
              >
                <h4 className={styles.panelTitle}>Deep Personality Facets</h4>
                <p className={styles.description}>
                  {!isPro
                    ? '⚠️ Upgrade to Pro to unlock the 120-question deep dive and see your 30 granular personality facets.'
                    : verificationLevel < 3
                      ? 'You have access! Take the deep dive assessment to reveal your 30 granular personality facets.'
                      : 'Your 30 granular personality facets from the full assessment.'}
                </p>
                <div className={styles.miniChartsGrid}>
                  <GatedFeature requiredTier="pro">
                    <MiniRadarChart
                      title="Openness"
                      facets={digitalLegacy.personalityData?.openness}
                      colorClass={verificationColor}
                    />
                  </GatedFeature>
                  <GatedFeature requiredTier="pro">
                    <MiniRadarChart
                      title="Conscientiousness"
                      facets={digitalLegacy.personalityData?.conscientiousness}
                      colorClass={verificationColor}
                    />
                  </GatedFeature>
                  <GatedFeature requiredTier="pro">
                    <MiniRadarChart
                      title="Extraversion"
                      facets={digitalLegacy.personalityData?.extraversion}
                      colorClass={verificationColor}
                    />
                  </GatedFeature>
                  <GatedFeature requiredTier="pro">
                    <MiniRadarChart
                      title="Agreeableness"
                      facets={digitalLegacy.personalityData?.agreeableness}
                      colorClass={verificationColor}
                    />
                  </GatedFeature>
                  <GatedFeature requiredTier="pro">
                    <MiniRadarChart
                      title="Neuroticism"
                      facets={digitalLegacy.personalityData?.neuroticism}
                      colorClass={verificationColor}
                    />
                  </GatedFeature>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className={styles.ctaCard}>
          <h2>Create Your Digital Passport</h2>
          <p>
            Your Digital Passport is your private, verified "passport" for the
            digital world. It's the core identity that powers your public
            profiles and enables personalized experiences across other apps.
          </p>
          <button
            onClick={handleGetStarted}
            className="action-button primary"
            disabled={isInitializing}
          >
            {isInitializing ? 'Initializing...' : 'Get Started'}
          </button>
        </div>
      )}

      {isEditModalOpen && (
        <EditPassportModal
          digitalLegacy={digitalLegacy}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSave}
        />
      )}

      {isStudioOpen && (
        <CreativeStudioModal
          onClose={(result) => {
            setIsStudioOpen(false);
            if (result) {
              navigate(`/chat/${digitalLegacy.personaId}`, {
                state: {
                  persona: digitalLegacy,
                  isOwner: true,
                },
              });
            }
          }}
          persona={digitalLegacy}
          initialData={studioInitialData}
        />
      )}

      <div className={styles.memoryCoreContainer}>
        <MemoryCoreWidget />
      </div>
    </div>
  );
}

export default withAuthenticationRequired(EgoProfilePage);
